// src/Dashboard.js
import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";

// Helper function to check if supabase client is properly configured
const checkSupabaseConnection = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

  const issues = [];

  if (!supabaseUrl)
    issues.push("Missing REACT_APP_SUPABASE_URL environment variable");
  if (!supabaseKey)
    issues.push("Missing REACT_APP_SUPABASE_ANON_KEY environment variable");

  if (!supabase?.supabaseUrl) issues.push("Supabase client missing URL");
  if (!supabase?.supabaseKey) issues.push("Supabase client missing API key");

  return {
    isConfigured: issues.length === 0,
    issues,
  };
};

function Dashboard() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState({
    checked: false,
    isConnected: false,
    message: "Not checked yet",
  });
  const [debug, setDebug] = useState({});

  // Check connection on mount
  useEffect(() => {
    const connection = checkSupabaseConnection();
    if (!connection.isConfigured) {
      setConnectionStatus({
        checked: true,
        isConnected: false,
        message: `Configuration issues: ${connection.issues.join(", ")}`,
      });
      return;
    }

    // Test connection with a simple query
    const testConnection = async () => {
      try {
        const { error } = await supabase
          .from("subscribers")
          .select("count", { count: "exact", head: true });

        // Check for specific errors
        if (error) {
          console.error("Connection test error:", error);
          if (error.code === "42P01") {
            setConnectionStatus({
              checked: true,
              isConnected: false,
              message:
                "Table not found. Database schema may not be initialized.",
            });
          } else if (error.code === "PGRST301") {
            setConnectionStatus({
              checked: true,
              isConnected: false,
              message:
                "Row Level Security policy prevented access. You may need to sign in.",
            });
          } else {
            setConnectionStatus({
              checked: true,
              isConnected: false,
              message: `Connection error: ${error.message} (${error.code})`,
            });
          }
        } else {
          setConnectionStatus({
            checked: true,
            isConnected: true,
            message: "Connected to Supabase",
          });
        }
      } catch (e) {
        console.error("Connection test exception:", e);
        setConnectionStatus({
          checked: true,
          isConnected: false,
          message: `Connection exception: ${e.message}`,
        });
      }
    };

    testConnection();
  }, []);

  // Fetch subscriptions and users on component mount
  useEffect(() => {
    async function fetchData() {
      if (!connectionStatus.checked || !connectionStatus.isConnected) {
        // Don't try to fetch data if we haven't confirmed connection works
        return;
      }

      setLoading(true);
      const debugInfo = {};

      // Log Supabase URL to verify connection
      console.log("Supabase Client Info:", {
        url: supabase.supabaseUrl,
        hasKey: !!supabase.supabaseKey,
      });

      // Fetch subscriptions
      try {
        const { data: subsData, error: subsError } = await supabase
          .from("subscribers")
          .select("*");

        debugInfo.subsQuery = {
          data: subsData,
          error: subsError,
          count: subsData?.length || 0,
        };
        console.log("Subscriptions query result:", debugInfo.subsQuery);

        if (subsError) {
          console.error("Error fetching subscriptions:", subsError);
          setError(
            `Subscriptions error: ${subsError.message} (${subsError.code})`
          );
        } else {
          setSubscriptions(subsData || []);
        }
      } catch (e) {
        console.error("Exception fetching subscriptions:", e);
        setError(`Subscriptions exception: ${e.message}`);
      }

      // Fetch users
      try {
        const { data: usersData, error: usersError } = await supabase
          .from("profiles")
          .select("*");

        debugInfo.usersQuery = {
          data: usersData,
          error: usersError,
          count: usersData?.length || 0,
        };
        console.log("Users query result:", debugInfo.usersQuery);

        if (usersError) {
          console.error("Error fetching users:", usersError);
          setError(
            (prev) =>
              prev || `Users error: ${usersError.message} (${usersError.code})`
          );
        } else {
          setUsers(usersData || []);
        }
      } catch (e) {
        console.error("Exception fetching users:", e);
        setError((prev) => prev || `Users exception: ${e.message}`);
      }

      // Fetch feedback
      try {
        const { data: feedbackData, error: feedbackError } = await supabase
          .from("feedback")
          .select("*");

        debugInfo.feedbackQuery = {
          data: feedbackData,
          error: feedbackError,
          count: feedbackData?.length || 0,
        };
        console.log("Feedback query result:", debugInfo.feedbackQuery);

        if (feedbackError) {
          console.error("Error fetching feedback:", feedbackError);
          setError(
            (prev) =>
              prev ||
              `Feedback error: ${feedbackError.message} (${feedbackError.code})`
          );
        } else {
          setFeedback(feedbackData || []);
        }
      } catch (e) {
        console.error("Exception fetching feedback:", e);
        setError((prev) => prev || `Feedback exception: ${e.message}`);
      }

      setDebug(debugInfo);
      setLoading(false);
    }

    fetchData();
  }, [connectionStatus.checked, connectionStatus.isConnected]);

  // Helper function to check table names in Supabase
  const checkTableNames = async () => {
    try {
      console.log("Supabase client info:", {
        url: supabase.supabaseUrl,
        hasKey: !!supabase.supabaseKey,
      });

      // Try querying each table directly to check if they exist and get row counts
      const tables = ["subscribers", "profiles", "feedback"];
      const results = {};

      for (const table of tables) {
        try {
          // First try a count query which is lightweight
          const { count, error: countError } = await supabase
            .from(table)
            .select("*", { count: "exact", head: true });

          if (countError) {
            console.error(`Count error for table ${table}:`, countError);
            results[table] = {
              exists: false,
              error: countError.message,
              code: countError.code,
            };
          } else {
            // If count works, try to get actual data
            const { data, error: dataError } = await supabase
              .from(table)
              .select("*")
              .limit(1);

            results[table] = {
              exists: true,
              count: count || 0,
              error: dataError ? dataError.message : null,
              hasData: !!data && data.length > 0,
              firstRow: data && data.length > 0 ? data[0] : null,
            };
          }

          console.log(`Table '${table}' check:`, results[table]);
        } catch (e) {
          console.error(`Error checking table ${table}:`, e);
          results[table] = { exists: false, error: e.message };
        }
      }

      // Check RLS policies that might be blocking access
      try {
        console.log("Checking authentication status...");
        const { data: authData } = await supabase.auth.getSession();
        console.log("Auth session:", authData);
      } catch (authErr) {
        console.error("Auth check error:", authErr);
      }

      // Display results in alert for easier viewing
      const summary = Object.entries(results)
        .map(([table, info]) => {
          if (!info.exists) return `${table}: ERROR - ${info.error}`;
          return `${table}: ${info.count} rows${
            info.hasData ? " (data available)" : " (empty results)"
          }`;
        })
        .join("\n");

      alert(`Table check results:\n${summary}\n\nSee console for details`);
    } catch (err) {
      console.error("Failed to check tables:", err);
      alert("Failed to check tables. See console for details.");
    }
  };

  if (!connectionStatus.checked) {
    return <div>Checking Supabase connection...</div>;
  }

  if (!connectionStatus.isConnected) {
    return (
      <div style={{ padding: "20px" }}>
        <h1>Database Connection Error</h1>
        <div style={{ color: "red", marginBottom: "20px" }}>
          {connectionStatus.message}
        </div>
        <button onClick={checkTableNames}>Run Diagnostics</button>
        <div style={{ marginTop: "20px" }}>
          <h2>Troubleshooting Tips</h2>
          <ol>
            <li>
              Check that your .env file has correct Supabase URL and API key
            </li>
            <li>Verify that your Supabase project is active and not paused</li>
            <li>
              Check if RLS (Row Level Security) is enabled but you're not
              authenticated
            </li>
            <li>Confirm your database tables exist with proper schema</li>
          </ol>
        </div>
      </div>
    );
  }

  if (loading) return <div>Loading dashboard data...</div>;

  if (error)
    return (
      <div>
        <div>Error: {error}</div>
        <button onClick={checkTableNames}>Run Diagnostics</button>
      </div>
    );

  return (
    <div style={{ padding: "20px" }}>
      <h1>Dashboard</h1>

      <div style={{ marginBottom: "20px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "10px",
          }}
        >
          <div
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: connectionStatus.isConnected ? "green" : "red",
              marginRight: "5px",
            }}
          ></div>
          <span>{connectionStatus.message}</span>
        </div>
        <button onClick={checkTableNames}>Run Diagnostics</button>
        <div style={{ marginTop: "10px" }}>
          <strong>Debug Info:</strong>
          <ul>
            <li>
              Subscriptions: {debug.subsQuery?.count || 0} records{" "}
              {debug.subsQuery?.error
                ? `(Error: ${debug.subsQuery.error.message})`
                : ""}
            </li>
            <li>
              Users: {debug.usersQuery?.count || 0} records{" "}
              {debug.usersQuery?.error
                ? `(Error: ${debug.usersQuery.error.message})`
                : ""}
            </li>
            <li>
              Feedback: {debug.feedbackQuery?.count || 0} records{" "}
              {debug.feedbackQuery?.error
                ? `(Error: ${debug.feedbackQuery.error.message})`
                : ""}
            </li>
          </ul>
        </div>
      </div>

      <section>
        <h2>
          Subscriptions{" "}
          {subscriptions.length === 0 ? "(Empty)" : `(${subscriptions.length})`}
        </h2>
        {subscriptions.length === 0 ? (
          <p>No subscription data available.</p>
        ) : (
          <table border="1" cellPadding="8" cellSpacing="0">
            <thead>
              <tr>
                <th>ID</th>
                <th>User ID</th>
                <th>Email</th>
                <th>Subscription Tier</th>
                <th>Subscribed</th>
                <th>End Date</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((sub) => (
                <tr key={sub.id}>
                  <td>{sub.id}</td>
                  <td>{sub.user_id}</td>
                  <td>{sub.email}</td>
                  <td>{sub.subscription_tier}</td>
                  <td>{sub.subscribed ? "Yes" : "No"}</td>
                  <td>
                    {sub.subscription_end
                      ? new Date(sub.subscription_end).toLocaleDateString()
                      : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section style={{ marginTop: "40px" }}>
        <h2>Users {users.length === 0 ? "(Empty)" : `(${users.length})`}</h2>
        {users.length === 0 ? (
          <p>No user data available.</p>
        ) : (
          <table border="1" cellPadding="8" cellSpacing="0">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Created At</th>
                <th>Updated At</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.email}</td>
                  <td>
                    {user.created_at
                      ? new Date(user.created_at).toLocaleDateString()
                      : "N/A"}
                  </td>
                  <td>
                    {user.updated_at
                      ? new Date(user.updated_at).toLocaleDateString()
                      : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section style={{ marginTop: "40px" }}>
        <h2>
          Feedback {feedback.length === 0 ? "(Empty)" : `(${feedback.length})`}
        </h2>
        {feedback.length === 0 ? (
          <p>No feedback available.</p>
        ) : (
          <table border="1" cellPadding="8" cellSpacing="0">
            <thead>
              <tr>
                <th>ID</th>
                <th>User ID</th>
                <th>Type</th>
                <th>Content</th>
                <th>Rating</th>
                <th>Resolved</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {feedback.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.user_id}</td>
                  <td>{item.feedback_type}</td>
                  <td>{item.content}</td>
                  <td>{item.rating}</td>
                  <td>{item.is_resolved ? "Yes" : "No"}</td>
                  <td>
                    {item.created_at
                      ? new Date(item.created_at).toLocaleDateString()
                      : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

export default Dashboard;
